import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MessageCircle, Send, X, Sparkles, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

let msgIdCounter = 0;
const genMsgId = () => `m_${Date.now()}_${++msgIdCounter}`;

const WELCOME_MSG = {
  id: 'welcome',
  role: 'assistant',
  content: "Hey there! I'm your NovaReel Buddy \u2014 popcorn ready? Tell me what you're in the mood for and I'll dig up something perfect. Or ask me anything about films & shows!",
};

const BuddyChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MSG]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('nrt_buddy_session') || null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages((prev) => [...prev, { id: genMsgId(), role: 'user', content: text }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/buddy/chat`, {
        message: text,
        session_id: sessionId,
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem('nrt_buddy_session', data.session_id);
      }
      setMessages((prev) => [...prev, { id: genMsgId(), role: 'assistant', content: data.reply }]);
    } catch (err) {
      console.error('[BuddyChat] send failed:', err);
      setMessages((prev) => [
        ...prev,
        { id: genMsgId(), role: 'assistant', content: "Sorry, I couldn't reach the projection booth just now. Try again in a moment." },
      ]);
    } finally { setSending(false); }
  };

  const startNewChat = () => {
    localStorage.removeItem('nrt_buddy_session');
    setSessionId(null);
    setMessages([WELCOME_MSG]);
  };

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[65] flex items-center gap-2 bg-gradient-to-br from-[#e11d48] to-[#7f1d1d] text-white pl-3 pr-4 py-3 rounded-full shadow-2xl hover:scale-105 active:scale-95 transition-transform"
          data-testid="buddy-open"
          aria-label="Open Novareel Buddy"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="hidden md:inline text-sm font-semibold">Your Novareel Buddy</span>
        </button>
      )}

      {/* Drawer */}
      {open && (
        <div className="fixed bottom-0 right-0 md:bottom-5 md:right-5 z-[66] w-full md:w-[380px] h-[85vh] md:h-[600px] bg-[#161616] border border-white/10 md:rounded-xl shadow-2xl flex flex-col overflow-hidden" data-testid="buddy-drawer">
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#e11d48]/25 to-transparent border-b border-white/10">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#e11d48] to-[#7f1d1d] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-white font-semibold text-sm leading-tight">Your Novareel Buddy</div>
                <div className="text-[10px] text-emerald-400">● Online · GPT-5.2</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={startNewChat} className="text-white/60 hover:text-white text-xs px-2 py-1 rounded hover:bg-white/10" title="New chat">New</button>
              <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white p-1 rounded hover:bg-white/10" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3 text-sm">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-2xl whitespace-pre-wrap leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-[#e11d48] text-white rounded-br-md'
                      : 'bg-white/5 text-white/95 border border-white/10 rounded-bl-md'
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white/5 border border-white/10 rounded-2xl rounded-bl-md px-3 py-2 text-white/60 text-sm inline-flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Buddy is thinking…
                </div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="border-t border-white/10 p-3 flex items-center gap-2 bg-[#141414]">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask for a recommendation…"
              className="flex-1 bg-[#222] text-white text-sm px-3 py-2.5 rounded-md outline-none focus:ring-2 focus:ring-[#e11d48]/60"
              data-testid="buddy-input"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="p-2.5 bg-[#e11d48] hover:bg-[#c9163f] disabled:opacity-50 rounded-md transition-colors"
              data-testid="buddy-send"
              aria-label="Send"
            >
              <Send className="w-4 h-4 text-white" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default BuddyChat;
